import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ServiceRecommendationEngine = ({ services, onRecommendation }) => {
  const [selectedChallenges, setSelectedChallenges] = useState([]);
  const [teamSize, setTeamSize] = useState('');
  const [urgency, setUrgency] = useState('');
  const [budget, setBudget] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const challengeOptions = [
    { value: 'slow-delivery', label: 'Slow feature delivery' },
    { value: 'technical-debt', label: 'High technical debt' },
    { value: 'poor-quality', label: 'Quality issues' },
    { value: 'team-dysfunction', label: 'Team dysfunction' },
    { value: 'scalability', label: 'Scalability problems' },
    { value: 'legacy-systems', label: 'Legacy system constraints' },
    { value: 'deployment-issues', label: 'Deployment challenges' },
    { value: 'communication', label: 'Poor communication' },
    { value: 'accountability', label: 'Lack of accountability' },
    { value: 'architecture', label: 'Architectural limitations' }
  ];

  const teamSizeOptions = [
    { value: '1-10', label: '1-10 engineers' },
    { value: '11-25', label: '11-25 engineers' },
    { value: '26-50', label: '26-50 engineers' },
    { value: '51-100', label: '51-100 engineers' },
    { value: '100+', label: '100+ engineers' }
  ];

  const urgencyOptions = [
    { value: 'immediate', label: 'Immediate (1-3 months)' },
    { value: 'short-term', label: 'Short-term (3-6 months)' },
    { value: 'medium-term', label: 'Medium-term (6-12 months)' },
    { value: 'long-term', label: 'Long-term (12+ months)' }
  ];

  const budgetOptions = [
    { value: 'startup', label: 'Startup ($50K-$200K)' },
    { value: 'growth', label: 'Growth ($200K-$500K)' },
    { value: 'enterprise', label: 'Enterprise ($500K-$1M)' },
    { value: 'large-enterprise', label: 'Large Enterprise ($1M+)' }
  ];

  const handleAnalyze = async () => {
    if (selectedChallenges.length === 0 || !teamSize || !urgency) return;

    setIsAnalyzing(true);
    
    // Simulate analysis
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Simple recommendation logic
    const recommendations = [];
    
    if (selectedChallenges.some(c => ['slow-delivery', 'technical-debt', 'deployment-issues'].includes(c))) {
      recommendations.push({
        service: services.find(s => s.id === 'velocity-engineering'),
        score: 95,
        reasoning: "Your challenges with delivery speed and technical debt align perfectly with our Velocity Engineering approach."
      });
    }
    
    if (selectedChallenges.some(c => ['team-dysfunction', 'communication', 'accountability'].includes(c))) {
      recommendations.push({
        service: services.find(s => s.id === 'ownership-culture'),
        score: 90,
        reasoning: "Team dynamics and accountability issues indicate a need for Ownership Culture Transformation."
      });
    }
    
    if (selectedChallenges.some(c => ['scalability', 'legacy-systems', 'architecture'].includes(c))) {
      recommendations.push({
        service: services.find(s => s.id === 'architectural-modernization'),
        score: 88,
        reasoning: "Scalability and architectural challenges require our Architectural Modernization expertise."
      });
    }

    // Sort by score
    recommendations.sort((a, b) => b.score - a.score);
    
    onRecommendation(recommendations);
    setIsAnalyzing(false);
  };

  const canAnalyze = selectedChallenges.length > 0 && teamSize && urgency;

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Brain" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Service Recommendation Engine</h3>
          <p className="text-sm text-text-secondary">Get personalized service recommendations based on your challenges</p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <Select
            label="What are your primary challenges?"
            description="Select all that apply to your current situation"
            options={challengeOptions}
            value={selectedChallenges}
            onChange={setSelectedChallenges}
            multiple
            searchable
            placeholder="Choose your challenges..."
            className="mb-4"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Team Size"
            options={teamSizeOptions}
            value={teamSize}
            onChange={setTeamSize}
            placeholder="Select team size..."
          />

          <Select
            label="Timeline"
            options={urgencyOptions}
            value={urgency}
            onChange={setUrgency}
            placeholder="Select timeline..."
          />
        </div>

        <div>
          <Select
            label="Budget Range (Optional)"
            description="Helps us recommend the most suitable engagement model"
            options={budgetOptions}
            value={budget}
            onChange={setBudget}
            placeholder="Select budget range..."
          />
        </div>

        <div className="pt-4 border-t border-border">
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={handleAnalyze}
            disabled={!canAnalyze}
            loading={isAnalyzing}
            iconName="Zap"
            iconPosition="left"
            className="bg-accent hover:bg-accent/90"
          >
            {isAnalyzing ? 'Analyzing Your Needs...' : 'Get Recommendations'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceRecommendationEngine;